import { ReactElement, useMemo } from 'react'
import { find } from 'lodash-es'
import { Box } from '@mui/material'
import usePlansQuery from '@editor/pages/Plans/hooks/usePlansQuery'
import { groupPlansByDate, planDateFormatter } from '@editor/pages/Plans/utils'
import { EmptyState } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { LoadingIndicator } from '@ui/feedback'
import { useEditorViewStore } from '@editorStores'
import { usePlanPdfViewer } from '../../PlanPdfViewerContext'
import useSavePlanPdf from '../../hooks/useSavePlanPdf'
import PlanFileRow from '../PlanFileRow'

/** "Pläne" tab: the project's saved plans, grouped into one section per day. */
const PlanViewPlansTab = (): ReactElement => {
  const { data: plans, isLoading } = usePlansQuery()
  const plansByDate = useMemo(() => groupPlansByDate(plans ?? []), [plans])
  const selectedPlanGuid = useEditorViewStore(state => state.planView.selectedPlanGuid)
  const setSelectedPlanGuid = useEditorViewStore(state => state.setPlanViewSelectedPlanGuid)
  const { changedDocument } = usePlanPdfViewer()
  const { savePlanPdf, isSaving } = useSavePlanPdf()

  // Switching plans saves the edited PDF first (the preview then loads the other plan); a failed
  // save keeps the edited plan selected so its changes aren't lost.
  const selectPlan = async (guid: string) => {
    if (guid === selectedPlanGuid || isSaving) return
    const changedPlan = changedDocument ? find(plans, { guid: changedDocument.guid }) : undefined
    if (changedPlan && !(await savePlanPdf(changedPlan))) return
    setSelectedPlanGuid(guid)
  }

  if (isLoading) {
    return (
      <Box height={150}>
        <LoadingIndicator />
      </Box>
    )
  }

  if (plansByDate.length === 0) {
    return <EmptyState sx={{ py: 2 }}>Keine gespeicherten Pläne vorhanden</EmptyState>
  }

  return (
    <Box sx={{ p: 1 }} data-cy="plan-view-plans-list">
      {plansByDate.map(([date, datePlans]) => (
        <Section
          key={date}
          title={planDateFormatter.format(new Date(date))}
          collapsible
          hideBorderBottom
        >
          {datePlans.map(plan => (
            <PlanFileRow
              key={plan.guid}
              plan={plan}
              selected={plan.guid === selectedPlanGuid}
              onClick={() => selectPlan(plan.guid)}
            />
          ))}
        </Section>
      ))}
    </Box>
  )
}

export default PlanViewPlansTab
